import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

import { AuthenticationService } from '../service/authentication.service';
import { ToastService } from '../../standalone-components/toast/service/toast.service';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  private readonly REFRESH_TOKEN_ENDPOINT = '/api/booking/public/auth/refresh';
  private readonly LOGOUT_ENDPOINT = '/api/booking/public/auth/logout';

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly toastService: ToastService,
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (req.url.includes(this.REFRESH_TOKEN_ENDPOINT)) {
      return next.handle(req);
    }

    const accessToken = this.authenticationService.getAccessToken();
    let requestToProcess = req;

    if (accessToken) {
      if (
        this.isAccessTokenExpired(accessToken) &&
        !req.url.includes(this.LOGOUT_ENDPOINT)
      ) {
        return this.handleExpiredToken(req, next);
      }

      requestToProcess = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
      });
    }

    return next.handle(requestToProcess).pipe(
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401 && accessToken) {
            return this.handleExpiredToken(req, next);
          }
          if (error.status === 500) {
            this.toastService.show($localize`Something went wrong`);
          }
        }
        return throwError(() => error);
      }),
    );
  }

  private handleExpiredToken(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return this.authenticationService.refreshToken().pipe(
      switchMap((accessToken) =>
        next.handle(
          req.clone({
            headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
          }),
        ),
      ),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 500) {
          this.toastService.show($localize`Something went wrong`);
        }

        return throwError(() => error);
      }),
    );
  }

  private isAccessTokenExpired(accessToken: string): boolean {
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }
}
